import React, { useState, useEffect } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import { DocumentService } from '@/services/documentService'
import { DocumentStats } from '@/types'
import { XCircle, BarChart3, RefreshCw } from 'lucide-react'

export const AdminDocumentStats: React.FC = () => {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [stats, setStats] = useState<DocumentStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      const data = await DocumentService.getDocumentStats()
      setStats(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load document statistics')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={pageStyles.container}>
        <div className="relative z-10 p-6">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: branding.primaryColor }}></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen" style={pageStyles.container}>
        <div className="relative z-10 p-6">
          <div className="p-6 mb-8 transition-all duration-300 border card backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.error.borderColor }}>
            <div className="flex items-start space-x-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: statusColors.error.backgroundColor }}>
                <XCircle className="w-6 h-6" style={{ color: statusColors.error.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mb-1 text-lg font-semibold" style={pageStyles.text}>Error loading statistics</h3>
                <p className="mb-4 text-sm" style={{ color: statusColors.error.color }}>{error}</p>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={loadStats}
                    className="px-3 py-1 rounded text-sm hover:opacity-80"
                    style={{ backgroundColor: statusColors.error.backgroundColor, borderColor: statusColors.error.borderColor, color: branding.textColor }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2 inline" />
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="min-h-screen" style={pageStyles.container}>
        <div className="relative z-10 p-6">
          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
            <div className="p-8 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full mb-4" style={{ backgroundColor: `${branding.surfaceColor}60` }}>
                <BarChart3 className="w-8 h-8" style={{ color: branding.textSecondaryColor }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={pageStyles.text}>No statistics available</h3>
              <p style={pageStyles.textSecondary}>Unable to load document statistics</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={pageStyles.container}>
      <div className="relative z-10 p-6">
        <div className="space-y-6">
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm p-6 text-center" style={{ ...cardStyles }}>
              <div className="text-3xl font-bold" style={{ color: branding.primaryColor }}>{stats.total_documents}</div>
              <div className="text-sm" style={pageStyles.textSecondary}>Total Documents</div>
            </div>

            <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm p-6 text-center" style={{ ...cardStyles }}>
              <div className="text-3xl font-bold" style={{ color: statusColors.warning.color }}>{stats.pending_documents}</div>
              <div className="text-sm" style={pageStyles.textSecondary}>Pending Review</div>
            </div>

            <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm p-6 text-center" style={{ ...cardStyles }}>
              <div className="text-3xl font-bold" style={{ color: statusColors.success.color }}>{stats.approved_documents}</div>
              <div className="text-sm" style={pageStyles.textSecondary}>Approved</div>
            </div>

            <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm p-6 text-center" style={{ ...cardStyles }}>
              <div className="text-3xl font-bold" style={{ color: statusColors.error.color }}>{stats.rejected_documents}</div>
              <div className="text-sm" style={pageStyles.textSecondary}>Rejected</div>
            </div>

            <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm p-6 text-center" style={{ ...cardStyles }}>
              <div className="text-3xl font-bold" style={{ color: branding.textSecondaryColor }}>{stats.expired_documents}</div>
              <div className="text-sm" style={pageStyles.textSecondary}>Expired</div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Documents by Status */}
            <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm p-6" style={{ ...cardStyles }}>
              <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>Documents by Status</h3>
              <div className="space-y-3">
                {Object.entries(stats.documents_by_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className="text-sm font-medium capitalize" style={pageStyles.textSecondary}>
                      {status.toLowerCase().replace('_', ' ')}
                    </span>
                    <div className="flex items-center space-x-2">
                      <div className="w-32 rounded-full h-2" style={{ backgroundColor: `${branding.surfaceColor}60` }}>
                        <div
                          className="h-2 rounded-full"
                          style={{
                            backgroundColor: status === 'PENDING'
                              ? statusColors.warning.color
                              : status === 'APPROVED'
                                ? statusColors.success.color
                                : status === 'REJECTED'
                                  ? statusColors.error.color
                                  : branding.textSecondaryColor,
                            width: `${(count / stats.total_documents) * 100}%`,
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium w-8" style={pageStyles.text}>{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents by Priority */}
            <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm p-6" style={{ ...cardStyles }}>
              <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>Documents by Priority</h3>
              <div className="space-y-3">
                {Object.entries(stats.documents_by_priority).map(([priority, count]) => (
                  <div key={priority} className="flex items-center justify-between">
                    <span className="text-sm font-medium capitalize" style={pageStyles.textSecondary}>
                      {priority.toLowerCase()}
                    </span>
                    <div className="flex items-center space-x-2">
                      <div className="w-32 rounded-full h-2" style={{ backgroundColor: `${branding.surfaceColor}60` }}>
                        <div
                          className="h-2 rounded-full"
                          style={{
                            backgroundColor: priority === 'REQUIRED'
                              ? statusColors.error.color
                              : priority === 'OPTIONAL'
                                ? branding.primaryColor
                                : statusColors.warning.color,
                            width: `${(count / stats.total_documents) * 100}%`,
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium w-8" style={pageStyles.text}>{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Documents by Type */}
          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm p-6" style={{ ...cardStyles }}>
            <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>Documents by Type</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(stats.documents_by_type)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 12)
                .map(([type, count]) => (
                  <div
                    key={type}
                    className="flex items-center justify-between p-3 rounded-lg border"
                    style={{ backgroundColor: `${branding.surfaceColor}60`, borderColor: `${branding.textSecondaryColor}30` }}
                  >
                    <span className="text-sm font-medium truncate" style={pageStyles.textSecondary}>
                      {type
                        .replace(/_/g, ' ')
                        .toLowerCase()
                        .replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <span className="text-sm font-bold" style={pageStyles.text}>{count}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
